import React, { FC } from 'react';
import styles from './index.module.css';
import { DesignAsset } from './types';
import { GroupedAssets } from './GroupedAssets';
import { UNGROUPED } from './AssetsScreen';
import { useDesignAssetsState } from '../../../clients/providers/DesignAssets';

type AssetsProps = {
  assets: DesignAsset[];
  data: DesignAsset[];
  disabled: boolean;
};

const Assets: FC<AssetsProps> = ({ assets, data, disabled }) => {
  const status = disabled ? 'archived' : 'created';

  const visibleGroupNames = data
    .filter(asset => asset.status === status)
    .reduce((result: string[], currentValue: DesignAsset) => {
      if (currentValue.groupName && !result.includes(currentValue.groupName)) {
        result.push(currentValue.groupName);
      }
      return result;
    }, []);

  const { viewGuestKanban, groupOrder, reorderGroups } = useDesignAssetsState();
  const groups = [
    ...groupOrder.filter(groupName => visibleGroupNames.includes(groupName)),
    ...visibleGroupNames.filter(groupName => !groupOrder.includes(groupName))
  ];

  const groupedAssets = groups.map(group => ({
    name: group,
    assets: assets.filter(
      asset => asset.groupName === group && asset.status === status
    )
  }));

  const ungroupedAssets = {
    name: UNGROUPED,
    assets: assets.filter(asset => !asset.groupName && asset.status === status)
  };

  const moveGroup = (groupName: string, direction: 'up' | 'down') => {
    const currentIndex = groups.indexOf(groupName);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (currentIndex === -1 || nextIndex < 0 || nextIndex >= groups.length) {
      return;
    }

    const nextGroupOrder = groups.slice();
    [nextGroupOrder[currentIndex], nextGroupOrder[nextIndex]] = [
      nextGroupOrder[nextIndex],
      nextGroupOrder[currentIndex]
    ];
    reorderGroups(nextGroupOrder);
  };

  return (
    <div className={styles.cardsContainer}>
      {groupedAssets.map(({ name, assets }, index) => {
        return (
          <GroupedAssets
            key={name}
            name={name}
            assets={assets}
            groups={groups}
            disabled={disabled}
            showDropdown={!viewGuestKanban}
            canMoveUp={index > 0}
            canMoveDown={index < groupedAssets.length - 1}
            onMoveUp={() => moveGroup(name, 'up')}
            onMoveDown={() => moveGroup(name, 'down')}
          />
        );
      })}
      <>
        <GroupedAssets
          name={ungroupedAssets.name}
          assets={ungroupedAssets.assets}
          groups={groups}
          disabled={disabled}
          key={'ungrouped'}
          showDropdown={false}
        />
      </>
    </div>
  );
};

export default Assets;
